import { useState, useContext, useMemo, useEffect, useCallback } from 'react';
import { message } from 'antd';
import { AccountType, ECreditType, UserCompanyType, isExternallyBilled, SubscriptionStatus } from '@leedpulse-monorepo/enums';
import SubscriptionContext from '../../../contexts/subscription/SubscriptionContext';
import { UserDisplay, UserCreditsInfo, userDocToUserDisplay } from '../types/UserDisplay';
import UserDoc from '../../../../domain/models/UserDoc';
import searchCompanyUsers from '../../../../domain/use_cases/users/searchCompanyUsers';
import { AddUserFormValues } from '../../../components/users/modals/add-user/AddUserModal';
import { EditUserFormValues } from '../../../components/users/modals/edit-user/EditUserModal';
import updateUserProfile from '../../../../domain/use_cases/users/updateUserProfile';
import CompanyContext from '../../../contexts/company/CompanyContext';
import AuthenticationContext from '../../../contexts/auth/AuthenticationContext';
import PlansContext from '../../../contexts/plans/PlansContext';
import { isUnlimitedLeedbucks } from '../../../helpers';
import createUser from '../../../../domain/use_cases/users/createUser';
import provisionCrmUser from '../../../../domain/use_cases/integrations/provisionCrmUser';
import { addNumbersToSubscription } from '../../../../domain/use_cases/stripe/addNumbersToSubscription';
import { assignUserToSubscription } from '../../../../domain/use_cases/subscription/assignUserToSubscription';
import { getSubscriptionOwnersContacts } from '../../../../domain/use_cases/subscription/getSubscriptionOwnersContacts';
import { ContactsTransferAnswer, useNumberAssignmentSteps } from '../../../hooks/subscription/useNumberAssignmentSteps';
import { removeUserFromCompany } from '../../../../domain/use_cases/users/removeUserFromCompany';
import { formatPhoneNumber, runningCreditsPromo } from '@leedpulse-monorepo/utils';
import { IPlan, IUser } from '@leedpulse-monorepo/interfaces';

export type ModalType =
  | 'ADD_USER'
  | 'VIEW_PROFILE'
  | 'EDIT_USER'
  | 'CHANGE_NUMBER'
  | 'DEACTIVATE_USER'
  | 'REACTIVATE_USER'
  | 'DELETE_USER';

export const useUsersPageController = () => {
  const { authentication, getUserCompanyRole, degradedSubscriptions } = useContext(AuthenticationContext);
  const { activeCompany, users, refreshUsers } = useContext(CompanyContext);
  const isCompanyExternallyBilled = isExternallyBilled(activeCompany?.partner_system, activeCompany?.id_stripe);

  // Returns true if a given subscription is degraded AND the company is not externally billed.
  const isSubDegraded = (subscriptionId: string | undefined) =>
    !!subscriptionId && !isCompanyExternallyBilled && degradedSubscriptions.some((d) => d.id === subscriptionId);
  const { subscriptions } = useContext(SubscriptionContext);
  const { planCache, fetchPlanById } = useContext(PlansContext);

  const [searchValue, setSearchValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(7);
  const [hits, setHits] = useState<(IUser & { avatar?: string })[]>([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [lastCreatedUserUid, setLastCreatedUserUid] = useState<string | null>(null);

  const isReadOnly = useMemo(() =>
    getUserCompanyRole(activeCompany?.id ?? '') !== UserCompanyType.SUPERVISOR
    , [activeCompany]);

  // Unified Modal & Loading State
  const [activeModal, setActiveModal] = useState<ModalType | null>(null);
  const [operationLoading, setOperationLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserDisplay | null>(null);
  const numberAssignment = useNumberAssignmentSteps();

  // Cache of plans resolved from subscriptions (plan_id → Plan)
  const [planMap, setPlanMap] = useState<Map<string, IPlan>>(new Map());

  // Build the list of available numbers (free only) — used by AddUserModal
  const availableNumbers = useMemo(() => {
    return subscriptions
      .filter((sub) => {
        if (sub.status === SubscriptionStatus.CANCELLED) return false;
        if (sub.status === SubscriptionStatus.PAUSED) return false;
        if (!sub.sender || sub.sender.length === 0) return false;
        const assignedUsers = users.filter((u) => u.idSubscription[sub.id]);
        return assignedUsers.length === 0;
      })
      .flatMap((sub) =>
        sub.sender.map((sender) => ({
          value: sub.id,
          // Prefer the visual plan name over the raw plan id (fallback to id if not resolved yet).
          label: `${formatPhoneNumber(sender)} — ${(planMap.get(sub.id_plan)?.name ?? sub.id_plan)}`,
          sender,
        }))
      );
  }, [subscriptions, users, planMap]);

  // Build the list of ALL numbers (with assigned user info) — used by ChangeNumberModal
  const allNumbers = useMemo(() => {
    return subscriptions
      .filter((sub) => sub.status !== SubscriptionStatus.CANCELLED && sub.status !== SubscriptionStatus.PAUSED && sub.sender && sub.sender.length > 0)
      .flatMap((sub) => {
        const assignedUsers = users.filter((u) => u.idSubscription[sub.id]);
        const assignedName = assignedUsers.length > 0
          ? `${assignedUsers[0].firstname} ${assignedUsers[0].lastname}`.trim()
          : null;

        return sub.sender.map((sender) => ({
          value: sub.id,
          label: assignedName
            ? `${formatPhoneNumber(sender)} — ${(planMap.get(sub.id_plan)?.name ?? sub.id_plan)} (${assignedName} assigné)`
            : `${formatPhoneNumber(sender)} — ${(planMap.get(sub.id_plan)?.name ?? sub.id_plan)}`,
          sender,
        }));
      });
  }, [subscriptions, users, planMap]);

  // Resolve plans for all subscriptions (async, stored in planMap)
  // planMap is intentionally excluded from deps — it would cause infinite re-fetching
  useEffect(() => {
    const uniquePlanIds = [...new Set(subscriptions.map(s => s.id_plan).filter(Boolean))];
    let cancelled = false;

    const resolve = async () => {
      const newEntries: [string, IPlan][] = [];
      for (const planId of uniquePlanIds) {
        const plan = await fetchPlanById(planId);
        if (plan && !cancelled) newEntries.push([planId, plan]);
      }
      if (!cancelled && newEntries.length > 0) {
        setPlanMap(prev => {
          const next = new Map(prev);
          newEntries.forEach(([k, v]) => next.set(k, v));
          return next;
        });
      }
    };
    resolve();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptions, fetchPlanById]);

  const fetchUsers = useCallback(async () => {
    if (!activeCompany?.id) return;
    setIsLoading(true);
    try {
      const { hits: fetchedHits, nbHits } = await searchCompanyUsers(
        { companyId: activeCompany.id, q: searchValue, page: currentPage - 1, hitsPerPage: pageSize },
        authentication,
      );
      setHits(fetchedHits);
      setTotalUsers(nbHits);
    } catch (error) {
      console.error('Error searching users:', error);
      setHits([]);
      setTotalUsers(0);
    } finally {
      setIsLoading(false);
    }
  }, [activeCompany?.id, authentication, searchValue, currentPage, pageSize]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchUsers();
    }, 400);
    return () => clearTimeout(timer);
  }, [fetchUsers]);

  // Freshly created user, taken from Firestore because it's not instantly synced in Algolia
  const lastCreatedUser = useMemo(() => {
    if (!lastCreatedUserUid || currentPage !== 1 || searchValue) return undefined;
    if (hits.some((hit) => hit.uid === lastCreatedUserUid)) return undefined;
    return users.find((user) => user.uid === lastCreatedUserUid);
  }, [lastCreatedUserUid, currentPage, searchValue, hits, users]);

  // Transform Algolia hits to UserDisplays with per-user plan & credits info
  const userDisplays = useMemo(() => {
    const userDocs = [...(lastCreatedUser ? [lastCreatedUser] : []), ...hits.map(hit => UserDoc.fromJson(hit))];
    // eslint-disable-next-line react/purity
    const iNow = Date.now();

    return userDocs.map(userDoc => {
      // Find the first Leedflow subscription assigned to this user
      const userSubIds = Object.keys(userDoc.idSubscription || {});
      const matchingSub = subscriptions.find(sub => userSubIds.includes(sub.id));

      let sender: string[] = [];
      let credits_info: UserCreditsInfo | null = null;

      if (matchingSub) {
        sender = matchingSub.sender || [];
        const plan = planMap.get(matchingSub.id_plan);
        if (plan) {
          // Same rule as the grant: the running promo replaces the plan's allocation
          const promo = runningCreditsPromo(matchingSub.leedbucks_credits_temp, iNow);
          credits_info = {
            remaining: { ...matchingSub.credits, [ECreditType.leedbucks]: matchingSub.credits?.[ECreditType.leedbucks] ?? 0 },
            total: promo ? { ...plan.credits, [ECreditType.leedbucks]: promo.leedbucks } : plan.credits,
            isUnlimitedLeedbucks: isUnlimitedLeedbucks(plan.credits?.[ECreditType.leedbucks] || 0),
            payg_enabled: matchingSub.settings?.payg_enabled,
          };
        }
      }

      return userDocToUserDisplay({
        userDoc,
        activeCompany: activeCompany || undefined,
        sender,
        subscription: matchingSub,
        credits_info,
        planCache,
      });
    });
  }, [hits, lastCreatedUser, subscriptions, activeCompany, planMap, planCache]);

  // Helper to open a modal and optional set user
  const openModal = (type: ModalType, user?: UserDisplay) => {
    if (user) setSelectedUser(user);
    setActiveModal(type);
  };

  // Helper to close modals and reset states
  const closeModal = () => {
    // A closed modal cancels its running assignment before anything is written
    numberAssignment.cancelAssignment();
    setActiveModal(null);
    setSelectedUser(null);
    setOperationLoading(false);
  };

  const handleAddUser = async (values: AddUserFormValues) => {
    setOperationLoading(true);

    if (!authentication || !activeCompany) {
      message.error('Veuillez vous connecter pour inviter un utilisateur');
      setOperationLoading(false);
      return;
    }

    // Block assignment on a degraded subscription (already filtered from availableNumbers, but guard here too).
    if (values.numberAssignment === 'existing' && isSubDegraded(values.selectedNumber)) {
      const deg = degradedSubscriptions.find((d) => d.id === values.selectedNumber);
      message.error(
        deg?.status === 'paused'
          ? "Impossible d'assigner ce numéro : l'abonnement est suspendu."
          : "Impossible d'assigner ce numéro : l'abonnement est résilié.",
      );
      setOperationLoading(false);
      return;
    }

    const iAssignment = numberAssignment.startAssignment();
    try {
      // The contacts left on an existing number by its previous owners go to this user or leave with their owner,
      // as the admin answers: asked before the account exists, so a cancelled invitation creates nothing
      const existingSub = values.numberAssignment === 'existing' && values.selectedNumber ? getSubscriptionById(values.selectedNumber) : undefined;
      if (values.numberAssignment === 'existing' && values.selectedNumber && !existingSub) {
        throw new Error('Abonnement sélectionné introuvable');
      }
      let transferContacts: ContactsTransferAnswer;
      if (existingSub) {
        const owners = await getSubscriptionOwnersContacts({ company_id: activeCompany.id, subscription_id: existingSub.id }, authentication);
        const newUserName = `${values.firstname ?? ''} ${values.lastname ?? ''}`.trim() || values.email;
        const answer = await numberAssignment.askContactsTransfer(iAssignment, owners.filter(o => o.contacts_count > 0), newUserName);
        // The modal was closed meanwhile: nothing is created
        if (answer === null) return;
        transferContacts = answer;
      }

      let userUid: string;

      if (values.crmUserId) {
        // ── CRM mode: provision via the integration endpoint ──
        if (!activeCompany) throw new Error('Aucune agence active');
        const provisionResponse = await provisionCrmUser({
          companyId: activeCompany.id,
          userId: values.crmUserId,
          email: values.email,
          firstname: values.firstname,
          lastname: values.lastname,
          phone: values.phone || undefined,
        }, authentication);
        const uid = provisionResponse?.data?.uid;
        if (!uid) throw new Error('Aucun UID utilisateur retourné');
        userUid = uid;
      } else {
        // ── Manual mode: create user via the standard endpoint ──
        // With a line, the credentials leave once it is assigned below, so the mail shows it
        const createUserResponse = await createUser({
          ...values,
          linked_company_id: activeCompany.id,
          type: AccountType.USER,
          skip_welcome_email: values.numberAssignment === 'new' || values.numberAssignment === 'existing',
        }, authentication);
        const uid = createUserResponse?.data?.uid;
        if (!uid) throw new Error('Aucun UID utilisateur retourné');
        userUid = uid;
      }

      // 2. Handle the number assignment based on the chosen option
      if (values.numberAssignment === 'new' && values.id_plan) {
        // ── "Commander un nouveau numéro" ──
        // → Add to Stripe subscription + create internal sub + assign user
        await addNumbersToSubscription({
          company_id: activeCompany.id,
          company_api_key: activeCompany.api_key,
          items: [{ plan_id: values.id_plan }],
          user_uid: userUid,
          user_phone: values.phone,
        }, authentication);
        message.success('Utilisateur invité et nouveau numéro commandé');

      } else if (existingSub) {
        // ── "Assigner un numéro existant" ──
        numberAssignment.setIsAssigning(true);
        await assignUserToSubscription({
          subscription: { id: existingSub.id, api_key: existingSub.api_key },
          user_uid: userUid,
          transfer_contacts: transferContacts,
        }, authentication);
        message.success('Utilisateur invité et numéro assigné');

      } else {
        // ── "Ne pas assigner de numéro" ──
        message.success('Utilisateur invité sans numéro');
      }

      setLastCreatedUserUid(userUid);
      await refreshUsers();
      await fetchUsers();
      closeModal();
    } catch (error) {
      console.error('Error inviting user:', error);
      message.error((error as Error).message || 'Erreur lors de l\'invitation de l\'utilisateur');
    } finally {
      setOperationLoading(false);
      numberAssignment.setIsAssigning(false);
    }
  };

  const handleConfirmReactivate = async () => {
    setOperationLoading(true);
    // TODO: Implement API call to reactivate user
    setTimeout(async () => {
      await refreshUsers();
      await fetchUsers();
      closeModal();
    }, 1000);
  };

  // Helper to look up a Subscription model from the subscriptions context by ID
  const getSubscriptionById = (subId: string) => subscriptions.find(s => s.id === subId);

  const handleConfirmChangeNumber = async (newSubscriptionId: string) => {
    if (!selectedUser || !authentication) {
      message.error('Utilisateur non sélectionné ou non authentifié');
      return;
    }

    // Block assignment to a degraded subscription (already filtered from allNumbers, but guard here too).
    if (isSubDegraded(newSubscriptionId)) {
      const deg = degradedSubscriptions.find((d) => d.id === newSubscriptionId);
      message.error(
        deg?.status === 'paused'
          ? "Impossible d'assigner ce numéro : l'abonnement est suspendu."
          : "Impossible d'assigner ce numéro : l'abonnement est résilié.",
      );
      return;
    }

    const newSub = getSubscriptionById(newSubscriptionId);
    if (!newSub) {
      message.error('Numéro introuvable');
      return;
    }

    setOperationLoading(true);
    const iAssignment = numberAssignment.startAssignment();
    try {
      // The contacts of the previous owners of the new number go to this user or leave with their owner, as the admin answers
      let transferContacts: ContactsTransferAnswer;
      if (activeCompany) {
        const owners = await getSubscriptionOwnersContacts({ company_id: activeCompany.id, subscription_id: newSub.id }, authentication);
        const answer = await numberAssignment.askContactsTransfer(iAssignment, owners.filter(o => o.uid !== selectedUser.userDoc.uid && o.contacts_count > 0), selectedUser.name);
        // The modal was closed meanwhile: nothing is written
        if (answer === null) return;
        transferContacts = answer;
      }
      numberAssignment.setIsAssigning(true);

      // Single backend call: assigns to new sub + optionally unassigns from old sub
      await assignUserToSubscription({
        subscription: { id: newSub.id, api_key: newSub.api_key },
        user_uid: selectedUser.userDoc.uid,
        old_subscription_id: selectedUser.idSubscription || undefined,
        transfer_contacts: transferContacts,
      }, authentication);

      const hasOldNumber = selectedUser.sender && selectedUser.sender.length > 0;
      message.success(hasOldNumber ? 'Numéro changé avec succès' : 'Numéro assigné avec succès');
      await refreshUsers();
      await fetchUsers();
      closeModal();
    } catch (error) {
      console.error('Error changing number:', error);
      message.error((error as Error).message || 'Erreur lors du changement de numéro');
    } finally {
      setOperationLoading(false);
      numberAssignment.setIsAssigning(false);
    }
  };

  const handleConfirmDeactivate = async () => {
    if (!selectedUser || !authentication) {
      message.error('Utilisateur non sélectionné ou non authentifié');
      return;
    }

    // Block unassignment if the user's subscription is degraded.
    const subIdToDeactivate = selectedUser.idSubscription;
    if (isSubDegraded(subIdToDeactivate)) {
      const deg = degradedSubscriptions.find((d) => d.id === subIdToDeactivate);
      message.error(
        deg?.status === 'paused'
          ? "Impossible de libérer ce numéro : l'abonnement est suspendu."
          : "Impossible de libérer ce numéro : l'abonnement est résilié.",
      );
      return;
    }

    setOperationLoading(true);
    const iAssignment = numberAssignment.startAssignment();
    try {
      const subId = selectedUser.idSubscription;
      if (!subId) {
        message.warning('Cet utilisateur n\'a pas de numéro assigné');
        closeModal();
        return;
      }

      const sub = getSubscriptionById(subId);
      if (!sub) {
        message.error('Abonnement introuvable');
        setOperationLoading(false);
        return;
      }

      // The contacts on the number stay on it or leave with their owner, as the admin answers
      let transferContacts: ContactsTransferAnswer;
      if (activeCompany) {
        const owners = await getSubscriptionOwnersContacts({ company_id: activeCompany.id, subscription_id: sub.id }, authentication);
        const answer = await numberAssignment.askContactsTransfer(iAssignment, owners.filter(o => o.contacts_count > 0), null);
        // The modal was closed meanwhile: nothing is written
        if (answer === null) return;
        transferContacts = answer;
      }
      numberAssignment.setIsAssigning(true);

      // Unassign user from their subscription → redirection = ''
      await assignUserToSubscription({
        subscription: { id: sub.id, api_key: sub.api_key },
        user_uid: 'none',
        transfer_contacts: transferContacts,
        old_user_uid: selectedUser.userDoc.uid,
      }, authentication);

      message.success(`${selectedUser.name} a été désactivé et son numéro libéré`);
      await refreshUsers();
      await fetchUsers();
      closeModal();
    } catch (error) {
      console.error('Error deactivating user:', error);
      message.error((error as Error).message || 'Erreur lors de la désactivation');
    } finally {
      setOperationLoading(false);
      numberAssignment.setIsAssigning(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedUser || !authentication || !activeCompany) {
      message.error('Utilisateur non sélectionné ou non authentifié');
      return;
    }

    setOperationLoading(true);
    try {
      await removeUserFromCompany({
        user_uid: selectedUser.userDoc.uid,
        company_id: activeCompany.id,
        company_api_key: activeCompany.api_key,
      }, authentication);

      message.success(`${selectedUser.name} a été supprimé de la compagnie`);
      await refreshUsers();
      await fetchUsers();
      closeModal();
    } catch (error) {
      console.error('Error removing user from company:', error);
      message.error((error as Error).message || "Erreur lors de la suppression de l'utilisateur");
    } finally {
      setOperationLoading(false);
    }
  };

  const handleSubmitEdit = async (values: EditUserFormValues) => {
    if (!selectedUser) return;

    setOperationLoading(true);
    try {
      const response = await updateUserProfile({
        uid: selectedUser.userDoc.uid,
        firstname: values.firstName,
        lastname: values.lastName,
      });

      if (response.success) {
        message.success('Utilisateur modifié avec succès');
        await refreshUsers();
        await fetchUsers();
        closeModal();
      } else {
        message.error(response.error || 'Erreur lors de la modification');
        setOperationLoading(false);
      }
    } catch (error) {
      console.error('Error updating user:', error);
      message.error('Erreur lors de la modification');
      setOperationLoading(false);
    }
  };

  const handleSearchChange = (value: string) => {
    setCurrentPage(1);
    setSearchValue(value);
  };

  const handlePageSizeChange = (size: number) => {
    setCurrentPage(1);
    setPageSize(size);
  };

  return {
    state: {
      searchValue,
      currentPage,
      pageSize,
      activeModal,
      operationLoading,
      transferQuestion: numberAssignment.transferQuestion,
      isAssigning: numberAssignment.isAssigning,
      selectedUser,
      availableNumbers,
      allNumbers,
      users: userDisplays,
      totalUsers: totalUsers + (lastCreatedUser ? 1 : 0),
      isLoading,
      isReadOnly,
      isCompanyExternallyBilled,
    },
    actions: {
      setSearchValue: handleSearchChange,
      setCurrentPage,
      setPageSize: handlePageSizeChange,
      openModal,
      closeModal,
      handleAddUser,
      handleConfirmReactivate,
      handleConfirmChangeNumber,
      answerContactsTransfer: numberAssignment.answerContactsTransfer,
      handleConfirmDeactivate,
      handleConfirmDelete,
      handleSubmitEdit,
    },
  };
};
